<template>
  <div :class="$style.base">
    <IconSpinner v-if="loading" />
    <template v-else-if="categories.length">
      <SelectCategory :class="$style.categories" @select="selectCategory" />
      <SelectServices
        :class="$style.services"
        @scroll="onScroll"
        @submit="$emit('submit')"
        @categoryScrolled="onCategoryScrolled"
      />
      <ServicesSummary
        v-if="settings.allow_multiple_services"
        @submit="$emit('submit')"
      />
    </template>
    <div v-else :class="$style.noResults" v-test="'no-services'">
      {{ $t('general.booking_disabled') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import IconSpinner from '@/components/IconSpinner.vue';
import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';
import { storeToRefs } from 'pinia';
import { provide, ref, watch } from 'vue';
import SelectCategory from './SelectCategory.vue';
import SelectServices from './SelectServices.vue';
import ServicesSummary from './ServicesSummary.vue';

defineOptions({ name: 'ChooseServices' });
const emit = defineEmits(['submit', 'scroll']);

const { settings } = useCompanyStore();

const { fetchServices } = useServicesStore();
const { categories, loading } = storeToRefs(useServicesStore());
const { resetDate } = useBookingStore();
const { resetAvailabilityStore } = useAvailabilityStore();

resetDate();
resetAvailabilityStore();
fetchServices();

const selectedCategoryId = ref<number>();
const clickedCategoryId = ref<number>();

provide('selectedCategoryId', selectedCategoryId);
provide('clickedCategoryId', clickedCategoryId);

watch(categories, () => {
  if (categories.value.length) {
    selectedCategoryId.value = categories.value[0].id;
  }
});

const isAnimating = ref(false);

provide('isAnimating', isAnimating);

const selectCategory = (id: number) => {
  if (isAnimating.value) {
    return;
  }

  selectedCategoryId.value = id;
  clickedCategoryId.value = id;

  isAnimating.value = true;
  setTimeout(() => {
    isAnimating.value = false;
  }, 700);
};

const scrollTop = ref(0);
provide('scrollTop', scrollTop);

const onScroll = (e: any) => {
  emit('scroll', e);
  scrollTop.value = e.target.scrollTop;
};

const onCategoryScrolled = (id: number) => {
  if (isAnimating.value) {
    return;
  }

  selectedCategoryId.value = id;
};
</script>

<style lang="scss" module>
.base {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.categories {
  flex-shrink: 0;
}

.services {
  height: 100%;
  overflow: hidden;
}

.noResults {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing;
}
</style>
